import { useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CURRENT_SEASON } from "@/lib/season";

export const STATUS = {
  ok: ["Materiali sufficienti", "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"],
  basso: ["Materiali in esaurimento", "border-amber-500/30 bg-amber-500/10 text-amber-400"],
  esaurito: ["Materiali esauriti", "border-rose-500/30 bg-rose-500/10 text-rose-400"],
  mancante: ["Distinta base mancante", "border-slate-500/30 bg-slate-500/10 text-slate-300"],
};

const Code = ({ code, testid }) => (
  <span className={`mr-2 shrink-0 font-mono-erp text-lg font-bold tracking-wider ${code ? "text-primary" : "text-amber-400"}`} data-testid={testid}>{code ? code.toUpperCase() : "SENZA CODICE"}</span>
);
// Codice fino alla stagione compresa (es. 8017-BIS-A26-VINO -> 8017 BIS A26)
const skuCode = (sku) => {
  const parts = (sku || "").split("-");
  const i = parts.findIndex((p, j) => j > 0 && /^[AP]\d{2}$/i.test(p));
  return parts.slice(0, i > 0 ? i + 1 : 2).join(" ");
};

// Zip: pezzi in magazzino per ogni lunghezza richiesta dalla scheda tecnica
const ZipCoverage = ({ r }) => {
  const zips = (r.materials || []).filter((m) => m.by_length?.length);
  if (!zips.length) return null;
  return (
    <div className="col-span-full space-y-0.5 pl-1 text-[11px]" data-testid={`coverage-zips-${r.sku}`}>
      {zips.map((z, i) => (
        <p key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
          <span className="text-muted-foreground">{z.name}{z.color ? ` ${z.color}` : ""}:</span>
          {z.by_length.map((l) => (
            <span key={l.length} className={l.pieces > 0 ? "" : "font-semibold text-rose-400"} data-testid={`coverage-zip-${r.sku}-${l.length}`}>
              {l.length} cm ({l.sizes.join(", ")}) <b>{num(l.available)}</b> pz → {l.pieces > 0 ? `${num(l.pieces)} capi` : "mancano"}
            </span>
          ))}
        </p>
      ))}
    </div>
  );
};

const Row = ({ r }) => (
  <div className="grid grid-cols-2 items-center gap-2 border-b border-border/40 py-2 text-sm last:border-0 md:grid-cols-[2fr_repeat(3,90px)_1.4fr_170px]" data-testid={`coverage-row-${r.sku}`}>
    <div className="col-span-2 min-w-0 md:col-span-1">
      <p className="flex min-w-0 items-baseline"><Code code={skuCode(r.sku)} testid={`coverage-code-${r.sku}`} /><span className="truncate font-medium">{r.name}</span></p>
      <p className="text-xs text-muted-foreground">{r.color || "—"} · {r.category}{r.bom_source === "scheda" && <span className={r.producible === null ? "text-amber-400" : "text-primary"}> · {r.producible === null ? "colore non scelto nella distinta della scheda tecnica" : "distinta dalla scheda tecnica"}</span>}</p>
    </div>
    <span className="text-xs md:text-right md:text-sm"><span className="text-muted-foreground md:hidden">Venduti </span>{num(r.sold)}</span>
    <span className="text-xs md:text-right md:text-sm"><span className="text-muted-foreground md:hidden">In magazzino </span>{num(r.stock)}</span>
    <span className="text-xs font-semibold md:text-right md:text-sm" data-testid={`coverage-producible-${r.sku}`}><span className="font-normal text-muted-foreground md:hidden">Producibili </span>{r.producible === null ? "—" : num(r.producible)}</span>
    <span className="truncate text-xs text-muted-foreground">{r.limiting ? `${r.limiting.name}: ${num(r.limiting.available)} ${r.limiting.unit || ""} (${num(r.limiting.per_unit)} a capo)` : "—"}</span>
    <Badge variant="outline" className={`justify-center text-[10px] ${STATUS[r.status][1]}`} data-testid={`coverage-status-${r.sku}`}>{STATUS[r.status][0]}</Badge>
    <ZipCoverage r={r} />
  </div>
);

const SheetRow = ({ r }) => (
  <div className="grid grid-cols-2 items-center gap-2 border-b border-border/40 py-2 text-sm last:border-0 md:grid-cols-[2fr_90px_1.4fr_170px]" data-testid={`coverage-sheet-row-${r.sku}`}>
    <div className="col-span-2 min-w-0 md:col-span-1">
      <p className="flex min-w-0 items-baseline"><Code code={r.code} testid={`coverage-sheet-code-${r.sku}`} /><span className="truncate font-medium">{r.name}</span></p>
      <p className="text-xs text-muted-foreground">{r.color || "Nessun colore nella distinta"}{r.missing > 0 && <span className="text-amber-400"> · {r.missing} materiali non scelti per questo colore</span>}</p>
    </div>
    <span className="text-xs font-semibold md:text-right md:text-sm" data-testid={`coverage-sheet-producible-${r.sku}`}><span className="font-normal text-muted-foreground md:hidden">Producibili </span>{r.producible === null ? "—" : num(r.producible)}</span>
    <span className="truncate text-xs text-muted-foreground">{r.limiting ? `${r.limiting.name}: ${num(r.limiting.available)} ${r.limiting.unit || ""} (${num(r.limiting.per_unit)} a capo)` : "—"}</span>
    <Badge variant="outline" className={`justify-center text-[10px] ${STATUS[r.status][1]}`}>{STATUS[r.status][0]}</Badge>
    <ZipCoverage r={r} />
  </div>
);

export const MaterialsCoverage = ({ testid = "materials-coverage" }) => {
  const [season, setSeason] = useState(CURRENT_SEASON);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const { data, isLoading } = useQuery({
    queryKey: ["materials", "coverage", season, dq],
    queryFn: async () => (await api.get("/materials/coverage", { params: { season, q: dq } })).data,
    placeholderData: keepPreviousData,
  });
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold"><Layers className="h-4 w-4 text-primary" />Copertura materiali · {dq ? "risultati della ricerca" : "articoli più venduti"}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca articolo, codice (es. 8001 a26), colore..." className="h-8 w-80 pl-9 text-xs" data-testid={`${testid}-search`} />
          </div>
        {data && (
          <Select value={season} onValueChange={setSeason}>
            <SelectTrigger className="h-8 w-24 text-xs" data-testid={`${testid}-season`}><SelectValue /></SelectTrigger>
            <SelectContent>{data.seasons.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        )}
        </div>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{dq ? "Tutti gli articoli della stagione che corrispondono alla ricerca, anche se non ancora venduti." : "I 20 capi (modello e colore) più venduti della stagione, accessori esclusi."} "Producibili" = capi che puoi ancora fare con la giacenza del materiale che finisce per primo. In esaurimento se ne puoi fare meno della metà di quelli venduti, o se manca una lunghezza di zip richiesta dalla scheda tecnica.</p>
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
        <>
          <div className="mb-3 flex flex-wrap gap-2">
            {Object.entries(data.summary).map(([k, v]) => <Badge key={k} variant="outline" className={STATUS[k][1]} data-testid={`${testid}-summary-${k}`}>{STATUS[k][0]}: {v}</Badge>)}
          </div>
          <div className="hidden grid-cols-[2fr_repeat(3,90px)_1.4fr_170px] gap-2 border-b border-border/60 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground md:grid">
            <span>Articolo</span><span className="text-right">Venduti</span><span className="text-right">In magazzino</span><span className="text-right">Producibili</span><span>Materiale limitante</span><span>Stato</span>
          </div>
          {data.items.map((r) => <Row key={r.id} r={r} />)}
          {!data.items.length && <p className="py-4 text-sm text-muted-foreground" data-testid={`${testid}-empty`}>{dq ? "Nessun articolo Shopify corrisponde alla ricerca" : "Nessuna vendita per questa stagione"}</p>}
          <div className="mt-6" data-testid={`${testid}-sheets`}>
            <h4 className="font-display text-base font-semibold">Modelli da scheda tecnica · non ancora su Shopify</h4>
            <p className="mb-2 text-xs text-muted-foreground">Per ogni colore della distinta base: capi che puoi fare con la giacenza attuale, usando metri e accessori per capo della scheda tecnica.</p>
            <div className="hidden grid-cols-[2fr_90px_1.4fr_170px] gap-2 border-b border-border/60 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground md:grid">
              <span>Modello</span><span className="text-right">Producibili</span><span>Materiale limitante</span><span>Stato</span>
            </div>
            {(data.sheets || []).map((r) => <SheetRow key={r.id} r={r} />)}
            {!data.sheets?.length && <p className="py-4 text-sm text-muted-foreground" data-testid={`${testid}-sheets-empty`}>{dq ? "Nessuna scheda tecnica corrisponde alla ricerca." : "Nessuna scheda tecnica di questa stagione senza prodotto su Shopify."}</p>}
          </div>
        </>
      )}
    </Card>
  );
};
